import React, { useMemo } from 'react';
import DataTable from 'react-data-table-component';
import { FaTable } from 'react-icons/fa';

const isPlainObject = value => value && typeof value === 'object' && !Array.isArray(value);

const mergeDeep = (base, override) => {
  if (!isPlainObject(base) || !isPlainObject(override)) {
    return override ?? base;
  }

  return Object.keys({ ...base, ...override }).reduce((result, key) => {
    result[key] = mergeDeep(base[key], override[key]);
    return result;
  }, {});
};

const defaultTableStyles = {
  table: {
    style: {
      backgroundColor: 'transparent',
    },
  },
  tableWrapper: {
    style: {
      display: 'block',
      backgroundColor: 'transparent',
    },
  },
  headRow: {
    style: {
      minHeight: '60px',
      borderBottomWidth: '1px',
      borderBottomColor: 'var(--border)',
      background:
        'linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.01)), var(--surface-deep)',
    },
  },
  headCells: {
    style: {
      paddingLeft: '1.1rem',
      paddingRight: '1.1rem',
      paddingTop: '0.95rem',
      paddingBottom: '0.95rem',
      color: 'var(--text-dim)',
      fontSize: '0.7rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },
  rows: {
    style: {
      minHeight: '74px',
      borderBottomWidth: '1px',
      borderBottomColor: 'var(--border)',
      backgroundColor: 'transparent',
      color: 'var(--text-strong)',
      transition: 'background-color 0.18s ease, box-shadow 0.18s ease',
    },
    highlightOnHoverStyle: {
      background:
        'linear-gradient(145deg, rgba(217, 255, 99, 0.06), rgba(83, 240, 156, 0.04)), var(--surface-alt)',
      boxShadow: 'inset 3px 0 0 rgba(217, 255, 99, 0.38)',
      transitionDuration: '0.16s',
      outline: 'none',
    },
  },
  cells: {
    style: {
      paddingLeft: '1.1rem',
      paddingRight: '1.1rem',
      paddingTop: '1rem',
      paddingBottom: '1rem',
      color: 'var(--text)',
      fontSize: '0.88rem',
      lineHeight: '1.45',
    },
  },
  progress: {
    style: {
      padding: '1.2rem',
      backgroundColor: 'transparent',
      color: 'var(--text-muted)',
    },
  },
  noData: {
    style: {
      padding: '1.6rem',
      backgroundColor: 'transparent',
    },
  },
  pagination: {
    style: {
      minHeight: '68px',
      borderTopWidth: '1px',
      borderTopColor: 'var(--border)',
      background:
        'linear-gradient(180deg, rgba(255, 255, 255, 0.03), transparent 120%), var(--surface-alt)',
      color: 'var(--text-muted)',
      fontSize: '0.78rem',
    },
    pageButtonsStyle: {
      width: '34px',
      height: '34px',
      margin: '0 0.12rem',
      borderRadius: '999px',
      color: 'var(--text-muted)',
      fill: 'var(--text-muted)',
      transition: 'all 0.18s ease',
      '&:disabled': {
        opacity: 0.4,
      },
      '&:hover:not(:disabled)': {
        backgroundColor: 'var(--accent-soft)',
        color: 'var(--accent-strong)',
        boxShadow: 'inset 0 0 0 1px rgba(217, 255, 99, 0.18)',
      },
      '&:focus': {
        outline: 'none',
        backgroundColor: 'var(--accent-soft)',
      },
    },
  },
};

function AdminDataTable({
  emptyIcon: EmptyIcon = FaTable,
  emptyTitle = 'No data found',
  emptyDescription = 'Belum ada data yang bisa ditampilkan pada halaman ini.',
  noDataComponent,
  pagination = true,
  highlightOnHover = true,
  pointerOnHover = true,
  responsive = true,
  persistTableHead = true,
  paginationComponentOptions,
  customStyles,
  shellClassName = '',
  tableMinWidth,
  ...props
}) {
  const resolvedTableStyles = useMemo(
    () => mergeDeep(defaultTableStyles, customStyles || {}),
    [customStyles]
  );

  const fallbackEmptyState = (
    <div className="admin-table-empty">
      <span className="admin-table-empty__icon">
        <EmptyIcon />
      </span>
      <strong>{emptyTitle}</strong>
      <p>{emptyDescription}</p>
    </div>
  );

  return (
    <div className={`admin-data-table-shell ${shellClassName}`.trim()}>
      <div className="admin-data-table-shell__scroll">
        <div
          className="admin-data-table-shell__inner"
          style={tableMinWidth ? { minWidth: tableMinWidth, width: '100%' } : undefined}
        >
          <DataTable
            pagination={pagination}
            highlightOnHover={highlightOnHover}
            pointerOnHover={pointerOnHover}
            responsive={responsive}
            persistTableHead={persistTableHead}
            customStyles={resolvedTableStyles}
            paginationComponentOptions={{
              rowsPerPageText: 'Baris per halaman',
              rangeSeparatorText: 'dari',
              ...paginationComponentOptions,
            }}
            noDataComponent={noDataComponent || fallbackEmptyState}
            {...props}
          />
        </div>
      </div>
    </div>
  );
}

export default AdminDataTable;
